import React, { useMemo, Component, useCallback } from "react"
import { ResponsiveBar } from "@nivo/bar"
import colors from "../../Constants/colors"

// Error Boundary to catch nivo internal errors
class BarGraphErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    // Don't break the UI for errors - just log them
    return { hasError: false, error: null }
  }

  componentDidCatch(error, errorInfo) {
    // Only log non-critical errors to avoid console spam
    if (error && error.message && !error.message.includes('tooltip')) {
      console.error('BarGraph Error:', error, errorInfo)
    }
  }

  render() {
    // Always render children - don't break the UI on errors
    return this.props.children
  }
}
/*
colors = [“color_a”,”color_b”,”color_c”, ect...]
keys = [“key_1”,”key_2”,ect…]
itemData = [
		{
			title: “To Be Displayed At Bottom”, //indexBy="title"
			itemOrder: [“key_1”,”key_2”, ect...],
			key_1: unitMeasurement,
			key_2: unitMeasurement,
			key_x: …,
		},
  ]
  
Need to keep track of Bar Graph max which would be 
*/

// unused function - calculates ticks at the top if design changes
// const calcTicks = (data, tickStep = 30, finalTick = true) => {
//   let maxTotal = 0
//   data.forEach(dataPoint => {
//     if (maxTotal < dataPoint.total) maxTotal = dataPoint.total
//     if (maxTotal < dataPoint.Sleep) maxTotal = dataPoint.Sleep
//   })
//   let ticks = []
//   for (let i = 0; i <= maxTotal; i += tickStep) {
//     ticks.push(i)
//   }
//   if (maxTotal % tickStep !== 0 && finalTick) ticks.push(maxTotal)
//   return ticks
// }

export default function CommitResponsiveBar({
  longestValue = 9,
  data,
  keys,
  inColors = ["lightgray"],
  tickStep,
  yUnits,
  xUnits,
  rightMarginModifier = 0,
  bottomMarginModifier = 0,
  small = false,
  vertical,
  percentage = true,
  fontColor,
  axisLeft,
  axisBottom,
  axisRight,
  axisTop,
  leftAxisLegendTitle = "",
  withLabel = false,
  fontSize
}) {
  // All hooks must be called before any conditional returns
  // Ensure inColors is an object (not an array) for proper color mapping
  const safeInColors = useMemo(() => {
    return (inColors && typeof inColors === 'object' && !Array.isArray(inColors)) 
      ? inColors 
      : { default: "#D8F0F8" }
  }, [inColors])
  
  // Ensure theme is always properly structured - memoize to prevent recreation
  const theme = useMemo(() => ({
    fontFamily: "Lato-Regular",
    fontSize: 16,
    textColor: fontColor || colors.white,
    fill: fontColor || colors.white,
    annotations: {
      text: {
        color: fontColor || colors.white,
        fill: colors.white,
        fontSize: 11,
        fontFamily: "Lato-Bold"
      }
    },
    tooltip: {
      container: {
        background: colors.white,
        color: colors.midnight,
        fontSize: 14,
        borderRadius: 4,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
        padding: "8px 12px"
      },
      basic: {
        whiteSpace: "pre",
        display: "flex",
        alignItems: "center"
      },
      table: {
        display: "table"
      },
      tableCell: {
        padding: "3px 5px"
      }
    },
    axis: {
      ticks: {
        line: {
          stroke: colors.white
        },
        text: {
          fill: fontColor || colors.white
        }
      },
      legend: {
        text: {
          fontSize: fontSize || 10,
          fill: fontColor || colors.white
        }
      }
    },
    labels: {
      text: {
        // textLength: 14,
      }
    }
  }), [fontColor, fontSize])
  
  // Ensure theme is always a valid object with complete tooltip structure - must be before early return
  // Nivo's BasicTooltip requires a fully structured theme.tooltip object
  const safeTheme = useMemo(() => {
    const defaultTooltipTheme = {
      container: {
        background: colors.white,
        color: colors.midnight,
        fontSize: 14,
        borderRadius: 4,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
        padding: "8px 12px"
      },
      basic: {
        whiteSpace: "pre",
        display: "flex",
        alignItems: "center"
      },
      table: {
        display: "table"
      },
      tableCell: {
        padding: "3px 5px"
      }
    }
    
    if (!theme || typeof theme !== 'object') {
      return {
        tooltip: defaultTooltipTheme
      }
    }
    
    // Deep merge to ensure all tooltip properties exist
    return {
      ...theme,
      tooltip: {
        ...defaultTooltipTheme,
        ...(theme.tooltip || {}),
        container: {
          ...defaultTooltipTheme.container,
          ...(theme.tooltip?.container || {})
        },
        basic: {
          ...defaultTooltipTheme.basic,
          ...(theme.tooltip?.basic || {})
        },
        table: {
          ...defaultTooltipTheme.table,
          ...(theme.tooltip?.table || {})
        },
        tableCell: {
          ...defaultTooltipTheme.tableCell,
          ...(theme.tooltip?.tableCell || {})
        }
      }
    }
  }, [theme])
  
  // Ensure data is a valid array before rendering
  // Handle undefined, null, or non-array values
  const safeData = (data && Array.isArray(data)) ? data : []
  const safeKeys = (keys && Array.isArray(keys) && keys.length > 0) ? keys : []
  
  // Simple, stable tooltip function - MUST be before early return
  // Always show percentage format in tooltip as whole numbers
  const tooltipFunction = useCallback(({ id, value, color, indexValue }) => {
    if (!id && !value) return null
    
    const safeValue = value != null ? Number(value) : 0
    // Convert decimal to percentage (multiply by 100) and round to whole number
    const formattedValue = `${Math.round(safeValue * 100)}%`
    
    const tooltipLabel = id && indexValue 
      ? `${id} - ${indexValue}` 
      : id || indexValue || 'Value'
    
    const safeColor = color || safeInColors.default || "#D8F0F8"
    
    return (
      <div style={{ 
        padding: '8px 12px', 
        background: colors.white, 
        color: colors.midnight, 
        borderRadius: 4,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
        fontSize: 14,
        fontFamily: "Lato-Regular"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 12, height: 12, background: safeColor, borderRadius: 2 }} />
          <span>{tooltipLabel}: {formattedValue}</span>
        </div>
      </div>
    )
  }, [safeInColors])
  
  // Memoize the colors function to prevent re-renders - MUST be before early return
  const colorsFunction = useCallback((itemData) => {
    if (!itemData || itemData.id === undefined || itemData.id === null) {
      return safeInColors.default || "#D8F0F8"
    }
    return safeInColors[itemData.id] || safeInColors.default || "#D8F0F8"
  }, [safeInColors])
  
  // Don't render if we don't have valid data or keys
  if (!safeData.length || !safeKeys.length) {
    return null
  }
  
  // Use data length as reset key to reset error boundary when data changes
  const resetKey = `${safeData.length}-${safeKeys.join(',')}`
  
  // Ensure safeTheme is always defined and is a plain object (not undefined or null)
  const finalTheme = safeTheme && typeof safeTheme === 'object' ? safeTheme : {
    tooltip: {
      container: {
        background: colors.white,
        color: colors.midnight,
        fontSize: 14,
        borderRadius: 4,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
        padding: "8px 12px"
      },
      basic: {
        whiteSpace: "pre",
        display: "flex",
        alignItems: "center"
      },
      table: {
        display: "table"
      },
      tableCell: {
        padding: "3px 5px"
      }
    }
  }
  
  // Render ResponsiveBar directly - error boundary was causing continuous re-renders
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <ResponsiveBar
        layout={vertical ? "vertical" : "horizontal"}
        theme={finalTheme}
        data={safeData}
        keys={safeKeys}
        indexBy="title"
        reverse={false}
        margin={{
          top: 0,
          right: 80 + rightMarginModifier,
          bottom: 50 + bottomMarginModifier,
          left: longestValue * 8 + 40
        }}
        padding={0.35}
        colors={colorsFunction}
        axisBottom={axisBottom || null}
        axisRight={axisRight || null}
        axisLeft={
          axisLeft || {
            ...null,
            tickSize: 0,
            tickPadding: 10,
            tickRotation: 0,
            legend: leftAxisLegendTitle,
            legendPosition: "middle",
            legendOffset: -longestValue * 7 - 15
          }
        }
        axisTop={axisTop || null}
        tooltip={tooltipFunction}
        labelFormat={(d) => {
          return <>{withLabel ? d : ""}</> // add d into the {} to render
        }}
        labelSkipWidth={small ? 50 : 30}
        labelSkipHeight={12}
        gridYValues={[]} // calcTicks(data, tickStep, false)}
        motionStiffness={10}
        motionDamping={5}
        />
    </div>
  )
}
